import { isLessThanSampleSizeLabel } from "utils/groupBy/isLessThanSampleSizeLabel";
import { isOthersGroupLabel } from "utils/groupBy/isOthersGroupLabel";

interface StackedBarLayoutLike {
  xAxis: { data: (string | number)[] };
}

interface StackedBarSeriesLike {
  label?: string;
  x?: unknown[];
  y?: unknown[];
}

/**
 * Removes the Others and Less Than N Sample Size x-axis categories from a stacked-bar
 * response, trimming every series' x/y in lockstep. A series named Others is the focus's
 * Others and stays.
 * Pure — never mutates its input.
 */
export function dropOthersFromStackedBar<
  S extends StackedBarSeriesLike,
  L extends StackedBarLayoutLike
>(series: S[], layout: L): { series: S[]; layout: L } {
  const categories = layout.xAxis.data;
  const keptIndexes = categories.reduce<number[]>((acc, label, index) => {
    if (!isOthersGroupLabel(String(label)) && !isLessThanSampleSizeLabel(String(label))) {
      acc.push(index);
    }
    return acc;
  }, []);
  const droppedAnyCategory = keptIndexes.length !== categories.length;

  const nextSeries = series.flatMap((s) => {
    if (!droppedAnyCategory) return [s];
    const trimmed = {
      ...s,
      x: Array.isArray(s.x) ? keptIndexes.map((index) => s.x[index]) : s.x,
      y: Array.isArray(s.y) ? keptIndexes.map((index) => s.y[index]) : s.y
    };
    // A series whose bars were all in Others would reach min/max as ±Infinity.
    return s.x?.length > 0 && trimmed.x.length === 0 ? [] : [trimmed];
  });

  return {
    series: nextSeries,
    layout: droppedAnyCategory
      ? {
          ...layout,
          xAxis: { ...layout.xAxis, data: keptIndexes.map((i) => categories[i]) }
        }
      : layout
  };
}
